import { styled, Divider } from '@withjoy/joykit';
import { Camera } from '@withjoy/joykit/icons';
import { animationTransition, animationTransitionExt } from '@shared/utils/animationTransition';
import { filledButtonStates } from '@apps/admin/routes/Dashboard/common/commonDasboard.styles';

export const AdminMenuRoot = styled.div`
  position: relative;
`;

export const AdminMenuButton = styled.button`
  position: relative;
  background-color: ${props => props.theme.colors.mono2};
  border: none;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 96px;
  height: 48px;
  padding: 0 8px 0 16px;

  &:hover {
    background-color: ${props => props.theme.colors.mono3};
  }

  &:active {
    background-color: ${props => props.theme.colors.mono4};
  }
`;

export const AdminMenuButtonV2 = styled(AdminMenuButton)`
  ${filledButtonStates};
  border-radius: 3rem;
  column-gap: 1rem;
  flex-direction: row-reverse;
  padding: 0.5rem 1.5rem 0.5rem 1rem;
  width: auto;
`;

export const HamburgerMenuIcon = styled('div')<{ isOpen: boolean }>`
  width: 24px;
  height: 18px;
  position: relative;
  cursor: pointer;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: ${props => props.theme.colors.black};
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transition: ${animationTransitionExt(
      { property: 'transform', duration: '250ms', timingFunction: 'ease-in-out' },
      { property: 'left', duration: '250ms', timingFunction: 'ease-in-out' },
      { property: 'width', duration: '250ms', timingFunction: 'ease-in-out' },
      { property: 'top', duration: '250ms', timingFunction: 'ease-in-out' }
    )};
  }

  span:nth-child(1) {
    top: 0;
  }

  span:nth-child(2),
  span:nth-child(3) {
    top: 8px;
  }

  span:nth-child(4) {
    top: 16px;
  }

  ${props =>
    props.isOpen && {
      'span:nth-child(1)': {
        top: '8px',
        width: 0,
        left: '50%'
      },

      'span:nth-child(2)': {
        transform: 'rotate(45deg)'
      },

      'span:nth-child(3)': {
        transform: 'rotate(-45deg)'
      },

      'span:nth-child(4)': {
        top: '8px',
        width: 0,
        left: '50%'
      }
    }}
`;

export const ButtonAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

export const AdminMenuDropdown = styled.div<{ isOpen: boolean }>`
  opacity: ${props => (props.isOpen ? 1 : 0)};
  transition: ${animationTransition('all')};
  transition-duration: 0.3s;
  transform-origin: left top;
  transform: ${props => (props.isOpen ? 'scaleY(1) scaleX(1)' : 'scaleY(0.9) scaleX(0.9)')};
  box-shadow: ${({ theme }) => `0 30px 60px ${theme.colors.mono1}40, 0 18px 36px ${theme.colors.mono14}20`};
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.mono3};
  position: absolute;
  top: 58px;
  min-width: 256px;
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  z-index: ${({ theme, isOpen }) => (isOpen ? theme.zIndices.dropdown : 'unset')};
  padding: 0.5rem 0;
  background-color: white;
  max-height: calc(100vh - 9rem);
  overflow: auto;
  right: 0;
`;

export const AdminMenuInfo = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const InfoPhoto = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

export const InfoPhotoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  height: 100%;
  width: 100%;
  background: ${props => props.theme.colors.white};
  opacity: 0.8;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  display: none;

  p {
    font-size: 0.8rem;
  }
`;

export const InfoPhotoOverlayIcon = styled(Camera)`
  width: 24px;
  height: 24px;
  margin-bottom: 5px;
`;

export const InfoPhotoWrapper = styled.a`
  width: 104px;
  height: 104px;
  border: 4px solid ${props => props.theme.colors.mono3};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.mono2};
  margin-bottom: 16px;
  position: relative;
  cursor: pointer;

  &:hover {
    ${InfoPhotoOverlay} {
      display: flex;
    }
  }
`;

export const AdminMenuNav = styled.nav`
  width: 100%;
  padding: 0 16px 16px;
  position: relative;
`;

export const AdminMenuNavList = styled.ul`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const AdminMenuNavListDivider = styled(Divider)`
  margin: 0 10px;
  width: unset;
`;

export const AdminMenuNavListItem = styled.li`
  position: relative;
  list-style: none;
  width: 100%;
`;

export const AdminMenuNavButton = styled.a`
  padding: 12px 8px;
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  letter-spacing: -0.01em;
  border: 2px solid ${props => props.theme.colors.white};
  border-radius: 4px;
  cursor: pointer;
  outline: 0;

  svg {
    width: 22px;
    height: 22px;
    margin-right: 10px;
  }

  &:focus {
    border-color: ${props => props.theme.colors.stroke};
  }

  &:hover {
    color: ${props => props.theme.colors.linkText};
    background: ${props => props.theme.colors.mono2};
    border-color: ${props => props.theme.colors.mono2};
  }

  &:active {
    color: ${props => props.theme.colors.linkActive};
    background: ${props => props.theme.colors.mono3};
    border-color: ${props => props.theme.colors.mono3};
  }
`;

export const NavButtonAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;
